import { $generateNodesFromDOM } from "@lexical/html"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isDecoratorNode,
  $isElementNode,
  $isTextNode,
  type LexicalNode,
  TextNode
} from "lexical"
import { useEffect, useRef } from "react"

import { JiraFieldKey } from "@/const/jira.constants"
import { $createClosedTagNode } from "../nodes/ClosedTagNode"

const $transformTextNodeWithTags = (node: TextNode): void => {
  const text = node.getTextContent()
  const TAG_PATTERN = /\{\{[^{}]*\}\}/g

  if (!TAG_PATTERN.test(text)) return

  TAG_PATTERN.lastIndex = 0

  const format = node.getFormat()
  const parts: LexicalNode[] = []
  let lastIndex = 0
  let match

  while ((match = TAG_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const beforeText = text.slice(lastIndex, match.index)
      const textNode = $createTextNode(beforeText)
      textNode.setFormat(format)
      parts.push(textNode)
    }

    const tagNode = $createClosedTagNode(match[0])
    tagNode.setFormat(format)
    parts.push(tagNode)
    lastIndex = TAG_PATTERN.lastIndex
  }

  if (lastIndex < text.length) {
    const afterText = text.slice(lastIndex)
    const textNode = $createTextNode(afterText)
    textNode.setFormat(format)
    parts.push(textNode)
  }

  if (parts.length > 0) {
    for (const part of parts) {
      node.insertBefore(part)
    }
    node.remove()
  }
}

const $processNodeForTags = (node: LexicalNode): void => {
  if ($isTextNode(node)) $transformTextNodeWithTags(node)
  else if ($isElementNode(node)) {
    const children = node.getChildren()

    for (const child of children) {
      $processNodeForTags(child)
    }
  }
}

export const InitialHtmlPlugin = ({ html }: { html?: string }) => {
  const [editor] = useLexicalComposerContext()
  const initialized = useRef(false)

  useEffect(() => {
    if (!html || initialized.current) return

    initialized.current = true

    queueMicrotask(() => {
      editor.update(() => {
        const parser = new DOMParser()
        const dom = parser.parseFromString(html, "text/html")
        const nodes = $generateNodesFromDOM(editor, dom)
        const root = $getRoot()
        const validNodes: LexicalNode[] = []

        root.clear()

        for (const node of nodes) {
          if ($isElementNode(node) || $isDecoratorNode(node))
            validNodes.push(node)
          else {
            const paragraph = $createParagraphNode()

            paragraph.append(node)
            validNodes.push(paragraph)
          }
        }

        root.append(...validNodes)

        for (const node of validNodes) {
          $processNodeForTags(node)
        }
      })

      queueMicrotask(() => {
        ;(
          document
            .querySelector(
              `[data-id='${JiraFieldKey.Summary}-dynamic-tags-field']`
            )
            ?.querySelector("div.editor-input") as HTMLElement | null
        )?.focus()
      })
    })
  }, [editor, html])

  return null
}
